// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { IfFeature } from '@tectonics/prs/feature';
import { useAllFeatures } from '@tectonics/prs/payment-spec';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { theme } from '@tectonics/look-and-feel/uber-v2/theme';

import {
  getLocalTaxCodeFromString,
  amountToCurrencyString,
  taxRateToPercentageString,
} from '@tectonics/prs/money';

import { useCurrentReceipt, getLocalTax } from '@tectonics/prs/receipts';

import { getReceiptAmountExt } from '@tectonics/prs/receipts/receipt';
import { getMoneyFormat } from '@tectonics/look-and-feel/uber-v2/commons/money-format';
import { TotalValue } from '@tectonics/look-and-feel/uber-v2/molecules/total-value';

export function InfoBox(): Node {
  const allFeatures = useAllFeatures();

  return (
    <InfoBoxWrapper data-testid="mobile_display_value">
      <DisplayReceiptAmountsWrapper>
        <DisplayValue>
          <IfFeature allFeatures={allFeatures} wanted="display_receipt_original_amount">
            <DisplayAmount />
          </IfFeature>
          <IfFeature allFeatures={allFeatures} wanted="display_receipt_brazilian_tax_iof">
            <DisplayTax code="iof" />
          </IfFeature>
        </DisplayValue>
      </DisplayReceiptAmountsWrapper>

      <Separator/>

      <DisplayValue data-testid="display_total_value">
        <DisplayTotalValueWrapper>
          <DisplayItem>
            <Label>Total</Label>
            <Value>
              <TotalValue />
            </Value>
          </DisplayItem>
        </DisplayTotalValueWrapper>
      </DisplayValue>

    </InfoBoxWrapper>
  );
}

function DisplayAmount(): Node {
  const incomingPayment = useIncomingPayment();
  const receipt = useCurrentReceipt();
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'za':
    case 'ke':
    case 'ng':
    case 'jm':
      return (
        <DisplayItem>
          <InfoItem>Amount</InfoItem>
          <InfoItem>
            {formatAmount(getReceiptAmountExt(receipt))}
          </InfoItem>
        </DisplayItem>
      );

    default:
      return (
        <DisplayItem>
          <InfoItem>Valor</InfoItem>
          <InfoItem>
            {formatAmount(getReceiptAmountExt(receipt))}
          </InfoItem>
        </DisplayItem>
      );
  }
}

type DisplayTaxProps = {|
  code: string,
  percentage?: string,
|};

function DisplayTax({ code, percentage }: DisplayTaxProps): Node {
  const incomingPayment = useIncomingPayment();
  const receipt = useCurrentReceipt();
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);
  const formatTax = taxRateToPercentageString(decimalFormat);
  const tax = getLocalTax(receipt, getLocalTaxCodeFromString(code));

  if(!tax) return null;

  const tax_percentage = formatTax(tax.rate);
  const percentage_value = percentage || tax_percentage;

  return (
    <DisplayItem>
      <InfoItem>{getTaxCodeLabel(code.toUpperCase())} ({percentage_value}%)</InfoItem>
      <InfoItem>
        {formatAmount(tax.amount)}
      </InfoItem>
    </DisplayItem>
  );
}

function getTaxCodeLabel(code: string): string {
  if (code === 'IG_IBP') {
    return 'Ganancias';
  }
  return code;
}

const InfoBoxWrapper = styled.div`
  background: ${theme.colors.gray(97)};
`;

const Label = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 20px;
  height: 36px;
  font-family: ${theme.fontFamily.display};
`;

const Value = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 24px;
  font-family: ${theme.fontFamily.display};
  height: 36px;
`;

const DisplayValue = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 12px;
`;

const DisplayReceiptAmountsWrapper = styled.div`
  background-color: white;
`;

const DisplayItem = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 4px 0;
  font-size: 14px;
  font-weight: 500;
  font-family: ${theme.fontFamily.sansSerif};
`;

const DisplayTotalValueWrapper = styled.div`
  color: ${theme.colors.new_primary[700]};
`;

const Separator = styled.div`
  border: 1px solid ${theme.colors.gray(95)};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 14px;
`;
